import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
    ScrollText, Loader2, AlertCircle, ChevronDown, ChevronUp, RefreshCw,
    Play, Pause, ArrowDownToLine, Search, FileWarning,
} from "lucide-react";

/**
 * Nextcloud Log Reader (live)
 * ---------------------------
 * Tails the cloud's Nextcloud JSON log from the worker VM and renders it
 * nicely. The path is resolved server-side via `occ log:file`, so it follows
 * whatever the instance is configured to use (standard deploy + imported
 * clouds). "Live" = polling GET .../logs/nextcloud every few seconds.
 */

const LINE_CHOICES = [100, 200, 500, 1000];
const POLL_MS = 3000;

// NC log levels → display. min-level used by the level filter.
const LEVELS = [
    { v: 0, key: "all", label: "All" },
    { v: 1, key: "info", label: "Info+" },
    { v: 2, key: "warn", label: "Warning+" },
    { v: 3, key: "error", label: "Error+" },
];

const LEVEL_STYLE = {
    debug: "text-zinc-400 bg-zinc-800/60 border-zinc-700",
    info: "text-sky-300 bg-sky-950/40 border-sky-900",
    warning: "text-amber-300 bg-amber-950/40 border-amber-900",
    error: "text-red-300 bg-red-950/40 border-red-900",
    fatal: "text-fuchsia-200 bg-fuchsia-950/50 border-fuchsia-800",
};

function fmtTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(11, 19) || "—";
    return d.toLocaleTimeString("en-US", { hour12: false });
}

export default function CloudLogReaderPanel({ cloudId, ncInstallState, defaultExpanded = false, bare = false }) {
    const [expanded, setExpanded] = useState(defaultExpanded || bare);
    const [live, setLive] = useState(true);
    const [lines, setLines] = useState(200);
    const [minLevel, setMinLevel] = useState(0);
    const [query, setQuery] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openIdx, setOpenIdx] = useState(null);

    const scrollRef = useRef(null);
    const stickToBottom = useRef(true);
    const ready = ncInstallState === "installed";

    const fetchLog = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(
                `/api/admin/clouds/${cloudId}/logs/nextcloud`,
                { params: { lines } },
            );
            setData(r.data);
            setError(null);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [cloudId, lines]);

    // Initial load + on lines change while open
    useEffect(() => {
        if (expanded && ready) fetchLog();
    }, [expanded, ready, fetchLog]);

    // Live polling
    useEffect(() => {
        if (!expanded || !live || !ready) return;
        const t = setInterval(fetchLog, POLL_MS);
        return () => clearInterval(t);
    }, [expanded, live, ready, fetchLog]);

    // Keep scrolled to the newest line while sticking to bottom
    useEffect(() => {
        const el = scrollRef.current;
        if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
    }, [data]);

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        stickToBottom.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    };

    const scrollToBottom = () => {
        const el = scrollRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
            stickToBottom.current = true;
        }
    };

    const entries = useMemo(() => data?.entries || [], [data]);
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return entries.filter((e) => {
            if (minLevel > 0 && e.level != null && e.level < minLevel) return false;
            if (!q) return true;
            return (
                (e.message || "").toLowerCase().includes(q) ||
                (e.app || "").toLowerCase().includes(q) ||
                (e.raw || "").toLowerCase().includes(q)
            );
        });
    }, [entries, minLevel, query]);

    return (
        <div className={bare ? "" : "cm-card p-5"} data-testid="cloud-log-reader-panel">
            {!bare && (
                <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setExpanded((x) => !x)}
                    data-testid="log-reader-toggle"
                >
                    <div className="cm-h3 flex items-center gap-2">
                        <ScrollText className="w-4 h-4 text-zinc-400" />
                        <span>Nextcloud Log Reader</span>
                        {live && expanded && ready && (
                            <span className="flex items-center gap-1 text-[11px] font-mono text-emerald-400 bg-emerald-950/40 border border-emerald-900 px-2 py-0.5 rounded">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                LIVE
                            </span>
                        )}
                        {!ready && (
                            <span className="text-[11px] font-mono text-amber-400 bg-amber-950/40 border border-amber-900 px-2 py-0.5 rounded">
                                waiting for install_state=installed
                            </span>
                        )}
                    </div>
                    {expanded ? (
                        <ChevronUp className="w-4 h-4 text-zinc-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-zinc-500" />
                    )}
                </div>
            )}

            {(expanded || bare) && (
                <>
                    {/* Controls */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setLive((x) => !x)}
                            disabled={!ready}
                            className={`cm-btn-ghost text-xs py-1 px-2 flex items-center gap-1 disabled:opacity-30 ${live ? "text-emerald-400" : "text-zinc-400"}`}
                            data-testid="log-live-toggle"
                        >
                            {live ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                            {live ? "Live on" : "Live off"}
                        </button>
                        <button
                            type="button"
                            onClick={fetchLog}
                            disabled={!ready || loading}
                            className="cm-btn-ghost text-xs py-1 px-2 flex items-center gap-1 disabled:opacity-30"
                            data-testid="log-refresh"
                        >
                            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            Refresh
                        </button>

                        {/* Level filter */}
                        <div className="flex items-center gap-1 bg-zinc-950 border border-zinc-800 rounded-md p-0.5" data-testid="log-level-filter">
                            {LEVELS.map((l) => (
                                <button
                                    key={l.key}
                                    type="button"
                                    onClick={() => setMinLevel(l.v)}
                                    className={`text-[11px] font-mono px-2 py-0.5 rounded ${minLevel === l.v ? "bg-zinc-700 text-zinc-100" : "text-zinc-500 hover:text-zinc-300"}`}
                                    data-testid={`log-level-${l.key}`}
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>

                        {/* Lines */}
                        <select
                            value={lines}
                            onChange={(e) => setLines(Number(e.target.value))}
                            disabled={!ready}
                            className="bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs font-mono rounded-md py-1 px-2 outline-none focus:border-zinc-600 disabled:opacity-30"
                            data-testid="log-lines-select"
                        >
                            {LINE_CHOICES.map((n) => (
                                <option key={n} value={n}>{n} lines</option>
                            ))}
                        </select>

                        {/* Search */}
                        <div className="relative flex-1 min-w-[160px]">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-600" />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Filter (message, app)…"
                                className="w-full bg-zinc-950 border border-zinc-800 focus:border-zinc-600 outline-none rounded-md py-1 pl-7 pr-2 text-xs font-mono text-zinc-200 placeholder:text-zinc-700"
                                data-testid="log-search-input"
                            />
                        </div>
                    </div>

                    {/* Meta row */}
                    <div className="mt-2 flex items-center justify-between text-[11px] font-mono text-zinc-500">
                        <span data-testid="log-file-path" className="truncate">
                            {data?.logfile ? (
                                <>File: <span className="text-zinc-400">{data.logfile}</span></>
                            ) : "—"}
                            {data ? <> · {filtered.length}/{entries.length} entries</> : null}
                        </span>
                        <button
                            type="button"
                            onClick={scrollToBottom}
                            className="flex items-center gap-1 hover:text-zinc-300"
                            data-testid="log-scroll-bottom"
                        >
                            <ArrowDownToLine className="w-3 h-3" /> End
                        </button>
                    </div>

                    {error && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-red-300 bg-red-950/20 border border-red-900/50 rounded-md px-3 py-2" data-testid="log-error">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span className="font-mono">{error}</span>
                        </div>
                    )}

                    {data?.missing && (
                        <div className="mt-3 flex items-start gap-2 text-xs text-amber-300 bg-amber-950/20 border border-amber-900/50 rounded-md px-3 py-2" data-testid="log-missing">
                            <FileWarning className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            <span>No log file found ({data.logfile}). Nothing may have been logged yet.</span>
                        </div>
                    )}

                    {/* Log body */}
                    <div
                        ref={scrollRef}
                        onScroll={onScroll}
                        className="mt-3 bg-zinc-950 border border-zinc-800 rounded-md max-h-[520px] overflow-auto font-mono text-xs divide-y divide-zinc-900/70"
                        data-testid="log-body"
                    >
                        {filtered.length === 0 && !error && (
                            <div className="px-3 py-6 text-center text-zinc-600" data-testid="log-empty">
                                {loading ? "Loading…" : "No entries."}
                            </div>
                        )}
                        {filtered.map((e, i) => (
                            <LogRow
                                key={i}
                                entry={e}
                                open={openIdx === i}
                                onToggle={() => setOpenIdx(openIdx === i ? null : i)}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

function LogRow({ entry, open, onToggle }) {
    const lvl = entry.level_name || "info";
    const style = LEVEL_STYLE[lvl] || LEVEL_STYLE.info;
    const hasDetail = !!(entry.exception || entry.raw);
    return (
        <div data-testid="log-row" className="hover:bg-zinc-900/40">
            <div
                className="flex items-start gap-2 px-3 py-1.5 cursor-pointer"
                onClick={onToggle}
            >
                <span className="text-zinc-600 shrink-0 tabular-nums" data-testid="log-row-time">
                    {fmtTime(entry.time)}
                </span>
                <span
                    className={`shrink-0 text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border ${style}`}
                    data-testid="log-row-level"
                >
                    {entry.level_name || "?"}
                </span>
                {entry.app && (
                    <span className="shrink-0 text-violet-300/80 bg-violet-950/30 px-1.5 py-0.5 rounded text-[11px]">
                        {entry.app}
                    </span>
                )}
                {entry._vm && (
                    <span className="shrink-0 text-sky-300/70 bg-sky-950/30 px-1.5 py-0.5 rounded text-[10px] font-mono" title={`VM: ${entry._vm}`}>
                        {entry._vm}
                    </span>
                )}
                <span className="text-zinc-200 break-all flex-1" data-testid="log-row-message">
                    {entry.message}
                </span>
                {hasDetail && (
                    open
                        ? <ChevronUp className="w-3 h-3 text-zinc-600 shrink-0 mt-0.5" />
                        : <ChevronDown className="w-3 h-3 text-zinc-600 shrink-0 mt-0.5" />
                )}
            </div>
            {open && (
                <div className="px-3 pb-2 space-y-2" data-testid="log-row-detail">
                    {(entry.user || entry.method || entry.url || entry.reqId) && (
                        <div className="text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-0.5">
                            {entry.user && <span>user: <span className="text-zinc-400">{entry.user}</span></span>}
                            {entry.method && <span>method: <span className="text-zinc-400">{entry.method}</span></span>}
                            {entry.url && <span className="break-all">url: <span className="text-zinc-400">{entry.url}</span></span>}
                            {entry.reqId && <span>reqId: <span className="text-zinc-400">{entry.reqId}</span></span>}
                        </div>
                    )}
                    {entry.exception && (
                        <pre className="text-[11px] text-red-300/90 whitespace-pre-wrap break-all bg-red-950/10 border border-red-900/30 rounded p-2 max-h-72 overflow-auto">
                            {entry.exception}
                        </pre>
                    )}
                    <pre className="text-[11px] text-zinc-500 whitespace-pre-wrap break-all bg-black/40 border border-zinc-900 rounded p-2 max-h-72 overflow-auto">
                        {entry.raw}
                    </pre>
                </div>
            )}
        </div>
    );
}
